<script setup>
import { onMounted, ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useFollowUpTemplatesStore } from 'dashboard/stores/followUpTemplates';

import SettingsLayout from '../SettingsLayout.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useFollowUpTemplatesStore();
const template = ref(null);
const saving = ref(false);
const generatingContent = ref(false);
const removeStepDialogRef = ref(null);
const stepToRemove = ref(null);

const form = reactive({
  name: '',
  description: '',
  anchor_kind: 'lead_qualified',
  pipeline_id: null,
  active: true,
});
const steps = ref([]);
const newStep = reactive({
  title: '',
  kind: 'call',
  delay_days: 0,
  description: '',
  auto_send: false,
  payload_template: '',
});

const AUTO_SENDABLE_KINDS = ['email', 'whatsapp'];
const isAutoSendableKind = computed(() =>
  AUTO_SENDABLE_KINDS.includes(newStep.kind)
);

// Tabs no padrão PipelineEditor - Geral / Passos da sequência
const activeTab = ref('general');
const TABS = [
  { key: 'general', label: 'Geral', icon: 'i-lucide-settings' },
  { key: 'steps', label: 'Passos', icon: 'i-lucide-list-checks' },
];

// Placeholders mostrados como `<code>` no editor. Como contêm `{{ }}` literais,
// não dá pra escrever direto no template Vue (o parser confunde com interpolação).
// Mantemos como constantes JS e usamos `v-text` no <code> para renderizar.
const PLACEHOLDER_HINTS = [
  '{{contact.first_name}}',
  '{{contact.name}}',
  '{{lead.summary}}',
  '{{lead.value_formatted}}',
  '{{owner.name}}',
];
// Placeholder do textarea - também precisa escapar das chaves duplas.
const TEMPLATE_PLACEHOLDER =
  'Olá {{contact.first_name}}, tudo bem? Sobre a sua proposta...';

const ANCHOR_KINDS = [
  { value: 'lead_qualified', label: 'Quando o lead é qualificado' },
  { value: 'manual_apply', label: 'Aplicado manualmente pelo agente' },
];

const STEP_KINDS = [
  { value: 'call', label: 'Ligação', icon: 'i-lucide-phone' },
  { value: 'email', label: 'E-mail', icon: 'i-lucide-mail' },
  { value: 'whatsapp', label: 'WhatsApp', icon: 'i-lucide-message-circle' },
  { value: 'visit', label: 'Visita', icon: 'i-lucide-map-pin' },
  { value: 'meeting', label: 'Reunião', icon: 'i-lucide-calendar' },
  { value: 'note', label: 'Nota', icon: 'i-lucide-sticky-note' },
];

function stepKindMeta(kind) {
  return STEP_KINDS.find(k => k.value === kind) || STEP_KINDS[0];
}

const breadcrumbItems = computed(() => [
  { label: 'Modelos de Follow-up', routeName: 'settings_follow_up_templates' },
  { label: template.value?.name || 'Editar modelo' },
]);

async function load() {
  template.value = await store.fetchOne(route.params.id);
  Object.assign(form, {
    name: template.value.name,
    description: template.value.description || '',
    anchor_kind: template.value.anchor_kind,
    pipeline_id: template.value.pipeline_id,
    active: template.value.active,
  });
  steps.value = [...(template.value.steps || [])];
}
onMounted(load);

function handleBreadcrumbClick(item) {
  if (item.routeName) router.push({ name: item.routeName });
}

async function save() {
  saving.value = true;
  try {
    const updated = await store.update(template.value.id, form);
    template.value = updated;
    steps.value = [...(updated.steps || [])];
    useAlert('Modelo salvo (nova versão criada).');
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao salvar.');
  } finally {
    saving.value = false;
  }
}

async function addStep() {
  if (!newStep.title.trim()) return;
  // Auto-send só vale para email/whatsapp. Para outros kinds, força false.
  const autoSend =
    newStep.auto_send && AUTO_SENDABLE_KINDS.includes(newStep.kind);
  if (autoSend && !newStep.payload_template.trim()) {
    useAlert('Defina o modelo da mensagem para envio automático.');
    return;
  }
  try {
    await store.createStep(template.value.id, {
      position: steps.value.length,
      title: newStep.title,
      kind: newStep.kind,
      delay_days: parseInt(newStep.delay_days, 10) || 0,
      description: newStep.description,
      auto_send: autoSend,
      payload_template: autoSend ? newStep.payload_template : null,
    });
    Object.assign(newStep, {
      title: '',
      kind: 'call',
      delay_days: 0,
      description: '',
      auto_send: false,
      payload_template: '',
    });
    await load();
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        e?.response?.data?.errors?.join?.(', ') ||
        'Falha ao adicionar passo.'
    );
  }
}

// Deixa a IA conduzir: gera o conteúdo do passo (payload_template) a partir do
// contexto do passo em edição. O usuário pode editar o texto depois.
async function generateContent() {
  generatingContent.value = true;
  try {
    const content = await store.generateStepContent(template.value.id, {
      title: newStep.title,
      kind: newStep.kind,
      delay_days: parseInt(newStep.delay_days, 10) || 0,
      description: newStep.description,
    });
    if (content) {
      newStep.payload_template = content;
      useAlert(t('PIPELINES.FOLLOW_UP.STEP.GENERATE_AI_SUCCESS'));
    }
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        t('PIPELINES.FOLLOW_UP.STEP.GENERATE_AI_ERROR')
    );
  } finally {
    generatingContent.value = false;
  }
}

function removeStep(step) {
  stepToRemove.value = step;
  removeStepDialogRef.value?.open();
}

async function confirmRemoveStep() {
  const step = stepToRemove.value;
  if (!step) return;
  try {
    await store.destroyStep(template.value.id, step.id);
    await load();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao remover.');
  } finally {
    removeStepDialogRef.value?.close();
    stepToRemove.value = null;
  }
}
</script>

<template>
  <SettingsLayout
    class="w-full max-w-3xl ltr:mr-auto rtl:ml-auto"
    :is-loading="!template"
  >
    <template #header>
      <div class="flex items-center gap-2 w-full justify-between mb-4 min-h-10">
        <Breadcrumb :items="breadcrumbItems" @click="handleBreadcrumbClick" />
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-4 pb-10">
        <!-- Cabeçalho compacto -->
        <div class="flex items-center gap-3">
          <span
            class="size-10 rounded-full bg-n-brand/10 flex items-center justify-center flex-shrink-0"
          >
            <i class="i-lucide-list-checks size-5 text-n-brand" />
          </span>
          <div class="min-w-0 flex-1">
            <h1
              class="text-heading-3 text-n-slate-12 truncate flex items-center gap-2 mb-0"
            >
              {{ template?.name }}
              <span
                class="text-label-small px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11 uppercase tracking-wide"
              >
                v{{ template?.version }}
              </span>
              <span
                v-if="!template?.active"
                class="text-label-small px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11 uppercase tracking-wide"
                >inativo</span
              >
            </h1>
            <p
              v-if="template?.description"
              class="text-body-small text-n-slate-11 truncate mb-0"
            >
              {{ template?.description }}
            </p>
          </div>
        </div>

        <!-- Tabs (padrão PipelineEditor) -->
        <nav class="flex gap-1 border-b border-n-weak">
          <button
            v-for="tab in TABS"
            :key="tab.key"
            type="button"
            class="flex items-center gap-2 px-3 py-2 text-body-main border-b-2 transition"
            :class="
              activeTab === tab.key
                ? 'border-n-brand text-n-slate-12 font-medium'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
            "
            @click="activeTab = tab.key"
          >
            <i :class="tab.icon" class="size-4" />
            {{ tab.label }}
          </button>
        </nav>

        <!-- Tab GERAL -->
        <div
          v-if="activeTab === 'general'"
          class="flex flex-col gap-4 max-w-2xl"
        >
          <WithLabel
            label="Nome"
            name="name"
            class="w-full"
          >
            <Input
              v-model="form.name"
              type="text"
              placeholder="Ex: Vendas Agressivo"
            />
          </WithLabel>

          <WithLabel
            label="Descrição"
            name="description"
            class="w-full"
          >
            <textarea
              v-model="form.description"
              rows="3"
              placeholder="Descreva quando e como este modelo deve ser usado"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-body-main text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
            />
          </WithLabel>

          <WithLabel
            label="Âncora"
            name="anchor_kind"
            class="w-full"
          >
            <SelectInput
              v-model="form.anchor_kind"
              :options="ANCHOR_KINDS"
              class="w-full [&>select]:w-full"
            />
          </WithLabel>

          <WithLabel
            label="Status"
            name="active"
            class="w-full"
          >
            <div class="inline-flex items-center gap-2">
              <ToggleSwitch v-model="form.active" />
              <span class="text-body-main text-n-slate-12">{{
                form.active ? 'Ativo' : 'Inativo'
              }}</span>
            </div>
          </WithLabel>

          <div class="pt-2">
            <NextButton
              :is-loading="saving"
              icon="i-lucide-save"
              size="sm"
              label="Salvar (cria nova versão)"
              @click="save"
            />
          </div>
        </div>

        <!-- Tab PASSOS -->
        <div v-if="activeTab === 'steps'" class="flex flex-col gap-4 max-w-2xl">
          <div class="flex items-center justify-between">
            <p class="text-body-small text-n-slate-11 mb-0">
              Ordem temporal de toques.
              <code class="px-1 py-0.5 rounded bg-n-alpha-2 text-label-small"
                >D+N</code
              >
              = N dias depois da âncora.
            </p>
            <span
              class="text-body-small text-n-slate-11 px-2 py-0.5 rounded-full bg-n-alpha-2"
            >
              {{ steps.length }} passo(s)
            </span>
          </div>

          <!-- Lista de passos existentes -->
          <div
            v-if="!steps.length"
            class="rounded-lg border border-dashed border-n-weak px-4 py-8 text-center"
          >
            <i
              class="i-lucide-list-plus size-6 text-n-slate-10 mb-2 mx-auto block"
            />
            <p class="text-body-small text-n-slate-11 mb-0">
              Nenhum passo configurado ainda - adicione abaixo.
            </p>
          </div>

          <ol v-else class="flex flex-col gap-2">
            <li
              v-for="step in steps"
              :key="step.id"
              class="rounded-lg border border-n-weak px-3 py-2.5 flex items-center gap-3 hover:border-n-slate-7 transition-colors"
            >
              <span
                class="size-8 rounded-full bg-n-alpha-2 flex items-center justify-center shrink-0"
              >
                <i
                  :class="stepKindMeta(step.kind).icon"
                  class="size-4 text-n-slate-12"
                />
              </span>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2 min-w-0">
                  <span
                    class="text-label-small uppercase tracking-wide text-n-slate-10 font-mono"
                    >D+{{ step.delay_days }}</span
                  >
                  <span
                    class="text-body-main font-medium text-n-slate-12 truncate"
                    >{{ step.title }}</span
                  >
                  <span
                    class="text-label-small px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11 uppercase tracking-wide"
                  >
                    {{ stepKindMeta(step.kind).label }}
                  </span>
                  <span
                    v-if="step.auto_send"
                    v-tooltip="'Envio automático no due_at'"
                    class="text-label-small px-1.5 py-0.5 rounded bg-n-brand/10 text-n-brand uppercase tracking-wide inline-flex items-center gap-1"
                  >
                    <i class="i-lucide-send size-3" /> auto
                  </span>
                </div>
                <p
                  v-if="step.description"
                  class="text-body-small text-n-slate-11 mt-0.5 mb-0 line-clamp-1"
                >
                  {{ step.description }}
                </p>
              </div>
              <NextButton
                faded
                ruby
                size="sm"
                icon="i-lucide-trash-2"
                @click="removeStep(step)"
              />
            </li>
          </ol>

          <!-- Form "Adicionar passo" - inline com WithLabel (padrão dos demais Settings).
               Tipo + Delay LADO A LADO em grid 2 cols, alinhados na mesma linha. -->
          <div class="mt-4 pt-4 border-t border-n-weak">
            <h3 class="text-heading-3 text-n-slate-12 mb-3">Adicionar passo</h3>
            <div class="flex flex-col gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-body-small font-medium text-n-slate-12">
                  Título
                </label>
                <Input
                  v-model="newStep.title"
                  type="text"
                  placeholder="Ex: Ligação de proposta"
                />
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div class="flex flex-col gap-1.5">
                  <label class="text-body-small font-medium text-n-slate-12">
                    Tipo
                  </label>
                  <SelectInput v-model="newStep.kind" :options="STEP_KINDS" />
                </div>
                <div class="flex flex-col gap-1.5">
                  <label class="text-body-small font-medium text-n-slate-12">
                    Delay (dias)
                  </label>
                  <Input
                    v-model="newStep.delay_days"
                    type="number"
                    min="0"
                  />
                </div>
              </div>

              <div class="flex flex-col gap-1.5">
                <label class="text-body-small font-medium text-n-slate-12">
                  Descrição
                </label>
                <textarea
                  v-model="newStep.description"
                  rows="2"
                  placeholder="Opcional - instruções para o agente"
                  class="!mb-0 w-full px-3 py-2 text-sm rounded-lg resize-y outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 hover:outline-n-slate-6 focus:outline-n-brand transition-all duration-200"
                />
              </div>

              <!-- Auto-send (só email/whatsapp) -->
              <div
                v-if="isAutoSendableKind"
                class="rounded-lg border border-dashed border-n-weak bg-n-alpha-1 p-3 flex flex-col gap-3"
              >
                <label class="inline-flex items-start gap-2 cursor-pointer">
                  <Checkbox v-model="newStep.auto_send" class="mt-0.5" />
                  <div>
                    <p class="text-body-main font-medium text-n-slate-12 mb-0">
                      Enviar automaticamente no due_at
                    </p>
                    <p class="text-label-small text-n-slate-11 mb-0">
                      A plataforma dispara a mensagem sem ação humana usando o
                      canal compatível com o contato. Disponível só para
                      {{ newStep.kind === 'email' ? 'e-mail' : 'WhatsApp' }}.
                    </p>
                  </div>
                </label>

                <div v-if="newStep.auto_send" class="flex flex-col gap-2">
                  <div class="flex items-center justify-between gap-2">
                    <label class="text-body-small text-n-slate-12 font-medium"
                      >Modelo da mensagem</label
                    >
                    <NextButton
                      v-tooltip="$t('PIPELINES.FOLLOW_UP.STEP.GENERATE_AI_HINT')"
                      faded
                      slate
                      size="sm"
                      icon="i-lucide-sparkles"
                      :is-loading="generatingContent"
                      :disabled="generatingContent"
                      :label="
                        generatingContent
                          ? $t('PIPELINES.FOLLOW_UP.STEP.GENERATING_AI')
                          : $t('PIPELINES.FOLLOW_UP.STEP.GENERATE_AI')
                      "
                      @click="generateContent"
                    />
                  </div>
                  <textarea
                    v-model="newStep.payload_template"
                    rows="4"
                    :placeholder="TEMPLATE_PLACEHOLDER"
                    class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-body-main text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand font-mono"
                  />
                  <p class="text-label-small text-n-slate-11 mb-0">
                    Placeholders disponíveis:
                    <code
                      v-for="(hint, hi) in PLACEHOLDER_HINTS"
                      :key="hint"
                      class="px-1 rounded bg-n-alpha-2"
                      :class="hi > 0 ? 'ml-1' : ''"
                    >{{ hint }}</code>
                  </p>
                </div>
              </div>

              <div class="flex justify-end">
                <NextButton
                  size="sm"
                  icon="i-lucide-plus"
                  label="Adicionar passo"
                  :disabled="!newStep.title.trim()"
                  @click="addStep"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <Dialog
      ref="removeStepDialogRef"
      type="alert"
      title="Remover passo"
      :description="`Remover o passo “${stepToRemove?.title}”? Esta ação não pode ser desfeita.`"
      confirm-button-label="Remover"
      cancel-button-label="Cancelar"
      @confirm="confirmRemoveStep"
    />
  </SettingsLayout>
</template>
